import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const LeaseSign = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedName, setTypedName] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/sign/${token}`)
      .then((res) => {
        setInfo(res.data);
        if (res.data.status === 'signed') setSigned(true);
      })
      .catch((err) =>
        setError(err.response?.data?.detail || 'This signing link is invalid.')
      )
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/sign/${token}`, {
        typed_name: typedName,
        agree,
      });
      setSigned(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit signature.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <p className="text-[13px] text-red-500">{error}</p>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="text-center">
          <p className="text-[15px] font-semibold text-slate-900">Lease signed</p>
          <p className="text-[13px] text-slate-500 mt-1">
            Thank you. A record of your signature has been saved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] py-10">
      <div className="max-w-lg mx-auto px-4">
        <div className="rounded-xl border border-slate-200/80 bg-white p-6">
          <h1 className="text-[15px] font-semibold text-slate-900 mb-1">
            Sign your lease — {info.facility_name}
          </h1>
          <p className="text-[13px] text-slate-500 mb-4">
            {info.customer_name}, please review the agreement and sign below.
          </p>

          {info.document_url && (
            <a
              href={`${API_URL}${info.document_url}`}
              target="_blank"
              rel="noreferrer"
              className="inline-block mb-4 text-[13px] text-blue-600 hover:text-blue-700 font-medium"
            >
              Review lease document (PDF)
            </a>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">
                Type your full legal name to sign
              </label>
              <input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Full name as on the account"
              />
            </div>
            <label className="flex items-start gap-2 text-[13px] text-slate-600">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                I agree this typed signature is the legal equivalent of my
                handwritten signature and that I am bound by this lease.
              </span>
            </label>
            {error && <p className="text-[13px] text-red-500">{error}</p>}
            <button
              onClick={submit}
              disabled={submitting || !agree || typedName.trim().length < 2}
              className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting…' : 'Sign lease'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaseSign;
